import _ from 'lodash'
import type { Component } from '@wix/thunderbolt-becky-types'
import { getCompType, isComponentFixed, isMasterPage } from './zIndexCompNodeHelpers'

import {
	ABOVE_ALL_IN_CONTAINER_Z_INDEX_DEFINED,
	ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED,
	INITIAL_Z_INDEX,
	RESPONSIVE_Z_INDEX_ADDITION,
	Z_INDEX_RANGE,
	PINNED_LAYER_SUFFIX,
	SECTIONS_CONTAINER_PREFIX,
	COMPONENT_TYPES,
	CSS_PROPERTIES,
	PINNED_LAYER_IN_CONTAINER,
} from './constants'
import { ZIndexCssChild, ZIndexCss, ZIndexPageChildren, ZIndexCssPage } from './zIndexCss.types'

// calc comp z-index value based on its index
const getZIndexValue: (index: number) => number = (index) =>
	index * Z_INDEX_RANGE + INITIAL_Z_INDEX + RESPONSIVE_Z_INDEX_ADDITION

// should add z-index to components that aren't of type 'Section' and are fixed or are header/footer and should have z-index
const getCompZindex = (
	zIndexChild: ZIndexCssChild,
	compIndex: number,
	headerSectionZIndex: number | undefined,
	footerSectionZIndex: number | undefined,
	isCompFixed: boolean,
	shouldFixPinnedSectionZIndex: boolean
) => {
	const compType = getCompType(zIndexChild.component, zIndexChild.refCompChildren)
	switch (compType) {
		case COMPONENT_TYPES.HEADER_SECTION:
			return isCompFixed ? getZIndexValue(compIndex) : headerSectionZIndex || -1
		case COMPONENT_TYPES.FOOTER_SECTION:
			return footerSectionZIndex ?? -1
		case COMPONENT_TYPES.SECTION:
			return shouldFixPinnedSectionZIndex && isCompFixed ? getZIndexValue(compIndex) : -1
		default:
			return isCompFixed ? getZIndexValue(compIndex) : -1
	}
}

const getZIndexMap = (
	pageOriginalChildren: ZIndexPageChildren,
	headerSectionZIndex: number | undefined,
	footerSectionZIndex: number | undefined,
	firstSectionIndex: number,
	isFixedByCompId: { [compId: string]: boolean },
	shouldFixPinnedSectionZIndex: boolean
): { [compId: string]: ZIndexCss } => {
	// filter all sections except the first one or pinned sections
	const filteredPageOriginalChildren: ZIndexPageChildren = pageOriginalChildren.filter((zIndexChild, index) => {
		const compType = getCompType(zIndexChild.component, zIndexChild.refCompChildren)
		return (
			compType !== COMPONENT_TYPES.SECTION ||
			index === firstSectionIndex ||
			(shouldFixPinnedSectionZIndex && isFixedByCompId[zIndexChild.component.id])
		)
	})
	return filteredPageOriginalChildren.reduce((acc: { [compId: string]: ZIndexCss }, zIndexChild, index) => {
		const zIndex = getCompZindex(
			zIndexChild,
			index,
			headerSectionZIndex,
			footerSectionZIndex,
			isFixedByCompId[zIndexChild.component.id] || false,
			shouldFixPinnedSectionZIndex
		)
		const compZIndex =
			zIndex > -1
				? {
						[CSS_PROPERTIES.Z_INDEX]: zIndex,
						[CSS_PROPERTIES.ABOVE_ALL_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_DEFINED,
				  }
				: {
						[CSS_PROPERTIES.ABOVE_ALL_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED,
				  }

		if (shouldFixPinnedSectionZIndex && isFixedByCompId[zIndexChild.component.id]) {
			compZIndex[CSS_PROPERTIES.PINNED_LAYER_IN_CONTAINER] = PINNED_LAYER_IN_CONTAINER
		}
		acc[zIndexChild.component.id] = compZIndex
		return acc
	}, {})
}

export const getResponsiveZIndexValues = (
	comp: Component,
	pageOriginalChildren: ZIndexPageChildren = [],
	shouldFixPinnedSectionZIndex: boolean
): ZIndexCssPage => {
	// Initializing all the variables and mappers we need
	const shouldRunResponsiveCalculation: boolean = !isMasterPage(comp)
	const isFixedByCompId: { [compId: string]: boolean } = {}
	const pageOriginalChildrenComps: Array<Component> = []
	const pageOriginalChildrenIdToCompType: { [compId: string]: string } = {}
	let headerSectionIndex = -1
	let firstSectionIndex = -1
	let footerSectionIndex = -1
	pageOriginalChildren.forEach((zIndexChild: ZIndexCssChild, index: number) => {
		// Getting the fixed children that should be on the pinned container
		isFixedByCompId[zIndexChild.component.id] = isComponentFixed(zIndexChild, { isResponsive: true })
		pageOriginalChildrenComps.push(zIndexChild.component)

		// Getting the comp type of the original children
		const compType = getCompType(zIndexChild.component, zIndexChild?.refCompChildren)
		pageOriginalChildrenIdToCompType[zIndexChild.component.id] = compType

		// Getting the index of the header section and the first section and the footer section
		if (compType === COMPONENT_TYPES.SECTION && firstSectionIndex === -1) {
			firstSectionIndex = index
		} else if (compType === COMPONENT_TYPES.HEADER_SECTION && headerSectionIndex === -1) {
			headerSectionIndex = index
		} else if (compType === COMPONENT_TYPES.FOOTER_SECTION && footerSectionIndex === -1) {
			footerSectionIndex = index
		}
	})

	let zIndexHeaderSection = -1
	let zIndexFooterSection = -1
	if (headerSectionIndex !== -1 && pageOriginalChildrenComps[headerSectionIndex]) {
		if (footerSectionIndex !== -1 && footerSectionIndex < headerSectionIndex) {
			zIndexHeaderSection = 1 + INITIAL_Z_INDEX
			zIndexFooterSection = INITIAL_Z_INDEX
		}
		// else, set higher z-index on the footer when at least one section is before the header
		else if (firstSectionIndex !== -1 && firstSectionIndex < headerSectionIndex) {
			zIndexHeaderSection = INITIAL_Z_INDEX
			zIndexFooterSection = 1 + INITIAL_Z_INDEX
		}
	}
	const zIndexMap: { [compId: string]: ZIndexCss } = getZIndexMap(
		pageOriginalChildren,
		zIndexHeaderSection,
		zIndexFooterSection,
		firstSectionIndex,
		isFixedByCompId,
		shouldFixPinnedSectionZIndex
	)
	if (shouldRunResponsiveCalculation) {
		// Maping the keys of the zIndexMap to the correct id
		const res = _(zIndexMap)
			.mapKeys((__, id) => {
				const compType = pageOriginalChildrenIdToCompType[id]
				if (compType === COMPONENT_TYPES.SECTION) {
					return `${SECTIONS_CONTAINER_PREFIX}${comp.id}`
				}
				// In case of a pinned layer, we need to map the id to the pinned layer id
				if (isFixedByCompId[id]) {
					return `${id}${PINNED_LAYER_SUFFIX}`
				}
				return id
			})
			.value()
		return res
	}
	return {}
}
